import { Capacitor } from '@capacitor/core';
import { useState, useEffect } from "react";
import { useLocation } from "wouter";
import { useAuth } from "@/hooks/use-auth";
import { EnhancedHeader } from "@/components/ui";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "@/hooks/use-toast";
import { AlertCircle, Info, Trash2, RefreshCcw, Clock } from "lucide-react";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";

export default function DeleteAccountPage() {
  const [_, setLocation] = useLocation();
  const { user, token, logoutMutation } = useAuth();
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [cancelLoading, setCancelLoading] = useState(false);
  const [error, setError] = useState("");
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [timeLeft, setTimeLeft] = useState<string>("");
  const [daysLeft, setDaysLeft] = useState<number | null>(null);
  const [deleteDate, setDeleteDate] = useState<Date | null>(null);
  const [isMarkedForDeletion, setIsMarkedForDeletion] = useState(false);
  
  // Check if user account is marked for deletion
  useEffect(() => {
    if (user?.markedForDelete) {
      setIsMarkedForDeletion(true);
      
      // Calculate time left and set countdown
      if (user.deleteDate) {
        const deletionDate = new Date(user.deleteDate);
        setDeleteDate(deletionDate);
        
        const updateCountdown = () => {
          const now = new Date();
          const timeRemaining = deletionDate.getTime() - now.getTime();
          
          if (timeRemaining <= 0) {
            // Account should be deleted by now
            setTimeLeft("Account should be deleted now");
            return;
          }
          
          // Calculate days, hours, minutes, seconds
          const days = Math.floor(timeRemaining / (1000 * 60 * 60 * 24));
          const hours = Math.floor((timeRemaining % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
          const minutes = Math.floor((timeRemaining % (1000 * 60 * 60)) / (1000 * 60));
          const seconds = Math.floor((timeRemaining % (1000 * 60)) / 1000);
          
          setDaysLeft(days);
          setTimeLeft(`${days}d ${hours}h ${minutes}m ${seconds}s`);
        };
        
        // Initial update
        updateCountdown();
        
        // Set interval for countdown
        const interval = setInterval(updateCountdown, 1000);
        return () => clearInterval(interval);
      }
    }
  }, [user]);
  
  const handleDeleteRequest = (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!username || !password) {
      setError("Please enter both username and password.");
      return;
    }
    
    if (username !== user?.username) {
      setError("Username doesn't match your account.");
      return;
    }
    
    // Show confirmation dialog instead of immediately deleting
    setShowConfirmDialog(true);
  };
  
  const handleCancelDeletion = async () => {
    setCancelLoading(true);
    setError("");
    
    try {
      // Check if we're in a mobile environment
      const baseUrl = Capacitor.isNativePlatform() ? import.meta.env.VITE_SERVER_URL : '';
      const response = await fetch(`${baseUrl}/api/delete-account/cancel`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        }
      });
      
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || "Failed to cancel account deletion");
      }
      
      toast({
        title: "Deletion Cancelled",
        description: "Your account deletion has been cancelled successfully.",
        variant: "default",
      });
      
      // Redirect to home page or refresh
      window.location.href = "/";
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to cancel account deletion. Please try again.");
    } finally {
      setCancelLoading(false);
    }
  };
  
  const handleDeleteAccount = async () => {
    setLoading(true);
    setError("");
    
    try {
      // Check if we're in a mobile environment
      const baseUrl = Capacitor.isNativePlatform() ? import.meta.env.VITE_SERVER_URL : '';
      const response = await fetch(`${baseUrl}/api/delete-account`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ username, password }),
      });
      
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || "Failed to delete account");
      }
      
      toast({
        title: "Account Deletion Initiated",
        description: "Your account has been marked for deletion. It will be permanently deleted after 14 days.",
        variant: "default",
      });
      
      // Use the same logout mutation as profile page
      logoutMutation.mutate();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to delete account. Please try again.");
      setShowConfirmDialog(false);
    } finally {
      setLoading(false);
    }
  };
  
  if (isMarkedForDeletion) {
    return (
      <div className="min-h-screen themed-gradient text-white pb-20">
        <EnhancedHeader
          title="Account Deletion"
          backPath="/user-settings"
          coloredText={true}
          rightContent={null}
          showBackButton={true}
        />
        
        <div className="max-w-md mx-auto px-4 py-6">
          <div className="mb-6 text-center">
            <div className="w-16 h-16 bg-red-950/40 rounded-full mx-auto flex items-center justify-center">
              <Clock className="h-10 w-10 text-red-500" />
            </div>
            <h1 className="text-2xl font-bold mt-4 text-red-400">Account Deletion In Progress</h1>
          </div>
          
          <Alert className="mb-6 border-red-500/50 bg-red-950/30">
            <AlertCircle className="h-4 w-4 text-red-500" />
            <AlertTitle className="text-red-400">Your Account Is Scheduled For Deletion</AlertTitle>
            <AlertDescription className="text-red-200/80">
              Your account is currently scheduled to be permanently deleted in:
            </AlertDescription>
            <div className="mt-3 text-center">
              <div className="text-2xl font-bold text-red-300">{timeLeft}</div>
              {deleteDate && (
                <div className="text-sm text-red-200/70">
                  Deletion date: {deleteDate.toLocaleDateString()} at {deleteDate.toLocaleTimeString()}
                </div>
              )}
            </div>
          </Alert>
          
          <div className="theme-card p-6 bg-black/30 border border-green-500/30 transition-colors mb-6">
            <h2 className="text-lg font-semibold mb-2 flex items-center gap-2 text-green-400">
              <RefreshCcw className="h-5 w-5" />
              You Can Still Recover Your Account
            </h2>
            <p className="text-gray-300">
              Your data hasn't been deleted yet. You can cancel the deletion process by clicking the button below to keep your account and all your data.
            </p>
          </div>
          
          <div className="pt-4">
            <Button
              type="button"
              variant="outline"
              className="w-full border-green-500/50 text-green-400 hover:bg-green-950/30"
              onClick={handleCancelDeletion}
              disabled={cancelLoading}
            >
              {cancelLoading ? "Processing..." : "Cancel Account Deletion"}
            </Button>
          </div>
          
          <div className="pt-4">
            <Button
              type="button"
              variant="ghost"
              className="w-full text-gray-400 hover:text-white"
              onClick={() => setLocation("/user-settings")}
            >
              Back to Settings
            </Button>
          </div>
          
          {error && (
            <Alert variant="destructive" className="mt-4">
              <AlertCircle className="h-4 w-4" />
              <AlertTitle>Error</AlertTitle>
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}
        </div>
      </div>
    );
  }
  
  return (
    <div className="min-h-screen themed-gradient text-white pb-20">
      <EnhancedHeader
        title="Delete Account"
        backPath="/user-settings"
        coloredText={true}
        rightContent={null}
        showBackButton={true}
      />
      
      <div className="max-w-md mx-auto px-4 py-6">
        <div className="mb-6 text-center">
          <div className="w-16 h-16 bg-red-950/40 rounded-full mx-auto flex items-center justify-center">
            <Trash2 className="h-10 w-10 text-red-500" />
          </div>
          <h1 className="text-2xl font-bold mt-4 text-red-400">Delete Account</h1>
        </div>
        
        <Alert className="mb-6 border-amber-500/50 bg-amber-950/20">
          <AlertCircle className="h-4 w-4 text-amber-500" />
          <AlertTitle className="text-amber-500">Warning: Permanent Action</AlertTitle>
          <AlertDescription className="text-amber-200/80">
            Deleting your account is a two-step process:
          </AlertDescription>
          <ul className="mt-2 ml-6 list-disc text-amber-200/80">
            <li>Your account will be marked for deletion immediately.</li>
            <li>Your account will be permanently deleted after 14 days.</li>
            <li>During the 14-day period, you can log in to cancel the deletion.</li>
            <li>After 14 days, all your personal data will be permanently removed.</li>
          </ul>
        </Alert>
        
        <div className="theme-card p-6 bg-black/30 border border-amber-500/30 transition-colors mb-6">
          <h2 className="text-lg font-semibold mb-2 flex items-center gap-2 text-yellow-400">
            <Info className="h-5 w-5" />
            14-Day Grace Period
          </h2>
          <p className="text-gray-300">
            After initiating deletion, you'll have 14 days to change your mind. Simply log in during this period to cancel the deletion process.
          </p>
        </div>
        
        <form onSubmit={handleDeleteRequest} className="space-y-4">
          {error && (
            <Alert variant="destructive" className="mb-4">
              <AlertCircle className="h-4 w-4" />
              <AlertTitle>Error</AlertTitle>
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}
          
          <div className="space-y-2">
            <label htmlFor="username" className="block text-sm font-medium">
              Enter your username to confirm
            </label>
            <Input
              id="username"
              type="text"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              placeholder={user?.username || "Your username"}
              className="w-full bg-black/50 border-gray-700 text-white"
              disabled={loading}
              required
            />
          </div>
          
          <div className="space-y-2">
            <label htmlFor="password" className="block text-sm font-medium">
              Enter your password to confirm
            </label>
            <Input
              id="password"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="Your password"
              className="w-full bg-black/50 border-gray-700 text-white"
              disabled={loading}
              required
            />
          </div>
          
          <div className="pt-4">
            <Button
              type="submit"
              variant="destructive"
              className="w-full"
              disabled={loading || !username || !password}
            >
              {loading ? "Processing..." : "Delete My Account"}
            </Button>
          </div>
          
          <div className="pt-2">
            <Button
              type="button"
              variant="ghost"
              className="w-full text-gray-400 hover:text-white"
              onClick={() => setLocation("/user-settings")}
              disabled={loading}
            >
              Cancel
            </Button>
          </div>
          
          <div className="text-center text-xs text-gray-400 mt-2">
            By clicking "Delete My Account", you acknowledge that you understand the consequences of deleting your account.
          </div>
        </form>
      </div>
      
      {/* Confirmation Dialog */}
      <Dialog open={showConfirmDialog} onOpenChange={setShowConfirmDialog}>
        <DialogContent className="bg-black/95 border-red-500/30 text-white">
          <DialogHeader>
            <DialogTitle className="text-red-400">Confirm Account Deletion</DialogTitle>
            <DialogDescription className="text-gray-300">
              Are you absolutely sure you want to delete your account? This action will mark your account for deletion.
            </DialogDescription>
          </DialogHeader>
          
          <div className="py-3">
            <p className="text-white/80">
              Your account will be permanently deleted after 14 days. During this period, you can log in to cancel the deletion process.
            </p>
          </div>
          
          <DialogFooter className="gap-2 sm:gap-0">
            <Button 
              variant="ghost"
              onClick={() => setShowConfirmDialog(false)} 
              disabled={loading}
            >
              Cancel
            </Button>
            <Button 
              variant="destructive"
              onClick={handleDeleteAccount}
              disabled={loading}
            >
              {loading ? "Processing..." : "Confirm Deletion"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>      
    </div>    
  ); 
}